import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import type { AnnotateResponse, ConnectSurfaceResponse, ScanOutcome } from './types';

/**
 * The slice's whole HTTP surface (`code-organization.md` — `api.ts` is the frontend's
 * `repository.ts`).
 *
 * ## Everything here needs a session, and no path carries a member id
 *
 * The member *is* the session, so there is nothing to supply and nothing to tamper with. The other
 * person in a connection is named by `attendeeRef`, which the API derives — the client never
 * composes one.
 *
 * ## There is no offline queue, and this file is where one would have to live
 *
 * `MEM-031` AC-3: *"a scan that cannot reach the service fails visibly and writes nothing."* So
 * {@link useScan} has **no `retry`, no optimistic update and no offline mutation persistence**. A
 * failed scan surfaces as a failure and the member taps again; nothing is held, nothing is
 * replayed, and there is no cache write that would make a screen show a connection the server does
 * not have. `EVT-031` AC-1 refuses the same thing on the organizer's side of the same room and the
 * same wifi — one decision, two clients.
 *
 * `retry: false` on the read for the reason the registrations slice gives: a 401 or a 404 is a
 * final answer, and retrying it three times only delays the message.
 */

const surfaceKey = (eventId: string): readonly unknown[] => ['member-in-event', 'connect', eventId];

/**
 * `MEM-028`, `MEM-030`, `MEM-031` — the whole Connect area, open or closed.
 *
 * **`refetchInterval` is what makes `MEM-030` AC-4's roster live.** The roster changes when
 * *somebody else* checks in or moves their toggle, and there is no push channel in this build, so
 * the only way a member sees the room fill up is to ask again. Twenty seconds is chosen against
 * the room rather than the server: it is short enough that walking up to somebody who just arrived
 * feels immediate, and long enough that a phone in a pocket for an hour makes 180 requests rather
 * than 3,600.
 *
 * It stops when the tab is hidden (`refetchIntervalInBackground` defaults to false), so a phone in
 * a pocket makes none at all.
 *
 * **No `staleTime`.** The ticket sets it to infinity because a ticket token is stable by
 * construction; this payload carries a roster and a notice list that are not.
 */
export function useConnectSurface(
  eventId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<ConnectSurfaceResponse> {
  return useQuery({
    queryKey: surfaceKey(eventId),
    queryFn: () =>
      apiRequest<ConnectSurfaceResponse>(
        `/api/member-in-event/events/${encodeURIComponent(eventId)}/connect`,
      ),
    enabled: options.enabled && eventId.length > 0,
    refetchInterval: 20_000,
    retry: false,
  });
}

/**
 * `MEM-029` AC-1 — **one tap.**
 *
 * One request, and the connection exists when it answers. There is no preflight resolve, no
 * confirmation and no second call: the mutation's own `onSuccess` refreshes the surface, and the
 * capture sheet renders from the outcome this returns. A `retry` here would be worse than useless
 * — it would turn one tap into several writes against a store whose idempotency is a primary key,
 * and it would hold the member's phone while it tried.
 *
 * The caller distinguishes "the server refused" from "the request never arrived": a refusal
 * resolves with `result: 'refused'`, and an unreachable server rejects. `constants.ts`'s
 * `nextScanStage` is where those two become different screens.
 */
export function useScan(eventId: string): UseMutationResult<ScanOutcome, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      apiRequest<ScanOutcome>(
        `/api/member-in-event/events/${encodeURIComponent(eventId)}/scans`,
        { method: 'POST', body: { token } },
      ),
    retry: false,
    onSuccess: async (outcome) => {
      // Only a scan that logged something changes the surface. Refreshing after a refusal would
      // put a request on the wire for nothing, in the place where the signal is worst.
      if (outcome.result === 'refused') return;
      await queryClient.invalidateQueries({ queryKey: surfaceKey(eventId) });
    },
  });
}

/**
 * `MEM-029` AC-3, AC-4 — the quick note and the follow-up intent, both optional.
 *
 * `PATCH` with only what the member gave, so an omitted field is left alone rather than cleared.
 * It is reached from the capture sheet **after** a connection exists, and from nowhere else that
 * could precede one.
 */
export function useAnnotateConnection(
  eventId: string,
): UseMutationResult<
  AnnotateResponse,
  Error,
  { readonly attendeeRef: string; readonly note?: string; readonly followUpIntent?: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ attendeeRef, note, followUpIntent }) =>
      apiRequest<AnnotateResponse>(
        `/api/member-in-event/events/${encodeURIComponent(eventId)}/connections/${encodeURIComponent(
          attendeeRef,
        )}`,
        {
          method: 'PATCH',
          // The shared client serializes `body` itself, so this is the object rather than a
          // string. Passing a pre-stringified one double-encodes it and the route's `.strict()`
          // schema rejects the result — which is exactly what happened on the first live run.
          body: {
            ...(note === undefined ? {} : { note }),
            ...(followUpIntent === undefined ? {} : { followUpIntent }),
          },
        },
      ),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: surfaceKey(eventId) });
    },
  });
}

/** `MEM-029` AC-5's dismissal. Removes a notice and never a connection — see the route's comment. */
export function useDismissNotice(eventId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attendeeRef: string) =>
      apiRequest<void>(
        `/api/member-in-event/events/${encodeURIComponent(eventId)}/notices/${encodeURIComponent(
          attendeeRef,
        )}`,
        { method: 'DELETE' },
      ),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: surfaceKey(eventId) });
    },
  });
}

/**
 * `MEM-030` AC-1, AC-2 — the toggle.
 *
 * It **returns the whole surface** and writes it straight into the cache, so the switch the member
 * moved and the roster they are looking at come from one answer. Two round trips is where a screen
 * starts disagreeing with itself.
 */
export function useSetOpenToConnect(
  eventId: string,
): UseMutationResult<ConnectSurfaceResponse, Error, boolean> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (openToConnect: boolean) =>
      apiRequest<ConnectSurfaceResponse>(
        `/api/member-in-event/events/${encodeURIComponent(eventId)}/open-to-connect`,
        { method: 'PUT', body: { openToConnect } },
      ),
    retry: false,
    onSuccess: (data) => {
      queryClient.setQueryData(surfaceKey(eventId), data);
    },
  });
}
